import { useState } from 'react';
import MainLayout from '../../../components/layout/MainLayout';
import SearchFilters from './components/SearchFilters';
import ProductTable from './components/ProductTable';
import ProductFormModal from './components/ProductFormModal';
import ConfirmModal from './components/ConfirmModal';
import { mockProducts, categories } from './mockData';
import type { Product } from './types';

export default function ProductListPage() {
  const [products, setProducts] = useState<Product[]>(mockProducts);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>(mockProducts);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [batchConfirmModal, setBatchConfirmModal] = useState<{
    show: boolean;
    type: 'online' | 'offline';
  }>({ show: false, type: 'online' });

  const handleSearch = (filters: {
    name: string;
    categoryId: number | null;
    isOnline: string;
    cpuCores: number | null;
    memory: number | null;
    gpu: string;
  }) => {
    let filtered = [...products];

    if (filters.name) {
      filtered = filtered.filter((p) => p.name.toLowerCase().includes(filters.name.toLowerCase()));
    }

    if (filters.categoryId) {
      filtered = filtered.filter((p) => p.categoryId === filters.categoryId);
    }

    if (filters.isOnline !== 'all') {
      filtered = filtered.filter((p) => p.isOnline === (filters.isOnline === 'online'));
    }

    if (filters.cpuCores) {
      filtered = filtered.filter((p) => p.cpuCores === filters.cpuCores);
    }

    if (filters.memory) {
      filtered = filtered.filter((p) => p.memory === filters.memory);
    }

    if (filters.gpu) {
      filtered = filtered.filter((p) => p.gpu === filters.gpu);
    }

    setFilteredProducts(filtered);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(filteredProducts.map((p) => p.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id: number, checked: boolean) => {
    if (checked) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter((i) => i !== id));
    }
  };

  const handleToggleStatus = (id: number, isOnline: boolean) => {
    const updated = products.map((p) => (p.id === id ? { ...p, isOnline } : p));
    setProducts(updated);
    setFilteredProducts(
      filteredProducts.map((p) => (p.id === id ? { ...p, isOnline } : p))
    );
  };

  const handleBatchOperation = (type: 'online' | 'offline') => {
    const isOnline = type === 'online';
    const updated = products.map((p) =>
      selectedIds.includes(p.id) ? { ...p, isOnline } : p
    );
    setProducts(updated);
    setFilteredProducts(
      filteredProducts.map((p) => (selectedIds.includes(p.id) ? { ...p, isOnline } : p))
    );
    setSelectedIds([]);
    setBatchConfirmModal({ show: false, type: 'online' });
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setShowFormModal(true);
  };

  const handleDelete = (id: number) => {
    const updated = products.filter((p) => p.id !== id);
    setProducts(updated);
    setFilteredProducts(filteredProducts.filter((p) => p.id !== id));
  };

  const handleSaveProduct = (productData: Partial<Product>) => {
    if (editingProduct) {
      const updated = products.map((p) =>
        p.id === editingProduct.id ? { ...p, ...productData } : p
      );
      setProducts(updated);
      setFilteredProducts(
        filteredProducts.map((p) => (p.id === editingProduct.id ? { ...p, ...productData } : p))
      );
    } else {
      const newProduct: Product = {
        id: Math.max(...products.map((p) => p.id)) + 1,
        createdAt: new Date().toISOString().replace('T', ' ').substring(0, 19),
        ...productData,
      } as Product;
      setProducts([...products, newProduct]);
      setFilteredProducts([...filteredProducts, newProduct]);
    }
    setShowFormModal(false);
    setEditingProduct(null);
  };

  return (
    <MainLayout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">商品列表</h1>
            <p className="text-sm text-gray-600 mt-1">管理云电脑商品和配置</p>
          </div>
          <button
            onClick={() => {
              setEditingProduct(null);
              setShowFormModal(true);
            }}
            className="px-6 py-2.5 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors flex items-center gap-2 whitespace-nowrap"
          >
            <i className="ri-add-line text-lg"></i>
            新建商品
          </button>
        </div>

        <SearchFilters categories={categories} onSearch={handleSearch} />

        <div className="bg-white rounded-lg p-4 mb-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setBatchConfirmModal({ show: true, type: 'online' })}
              disabled={selectedIds.length === 0}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              批量上架
            </button>
            <button
              onClick={() => setBatchConfirmModal({ show: true, type: 'offline' })}
              disabled={selectedIds.length === 0}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              批量下架
            </button>
            {selectedIds.length > 0 && (
              <span className="text-sm text-gray-600">已选择 {selectedIds.length} 项</span>
            )}
          </div>
        </div>

        <ProductTable
          products={filteredProducts}
          selectedIds={selectedIds}
          onSelectAll={handleSelectAll}
          onSelectOne={handleSelectOne}
          onToggleStatus={handleToggleStatus}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </div>

      <ProductFormModal
        isOpen={showFormModal}
        product={editingProduct}
        onClose={() => {
          setShowFormModal(false);
          setEditingProduct(null);
        }}
        onSave={handleSaveProduct}
      />

      <ConfirmModal
        isOpen={batchConfirmModal.show}
        title="确认批量操作"
        message={`确定要批量${batchConfirmModal.type === 'online' ? '上架' : '下架'}选中的 ${
          selectedIds.length
        } 个商品吗？`}
        onConfirm={() => handleBatchOperation(batchConfirmModal.type)}
        onCancel={() => setBatchConfirmModal({ show: false, type: 'online' })}
      />
    </MainLayout>
  );
}
